import React from "react";

/**
 * Loader shown in the Insights tab while recommendations are still being
 * generated. Dotted orbit + bouncing dots on top, shimmering skeleton cards
 * below so the panel keeps its final shape while data is in flight.
 */

const ROTATING_MESSAGES = [
  "Crunching your traffic data…",
  "Looking for conversion leaks…",
  "Reading heatmaps & journeys…",
  "Ranking the highest-impact fixes…",
];

const SkeletonCard = ({ delay = 0 }) => (
  <div
    className="bg-white rounded-2xl border border-slate-200/70 p-4 shadow-sm animate-fade-in"
    style={{ animationDelay: `${delay}ms`, animationFillMode: "backwards" }}
  >
    <div className="flex items-start gap-3">
      {/* Icon tile */}
      <div className="w-10 h-10 rounded-xl cf-shimmer shrink-0" />
      <div className="flex-grow space-y-2.5 pt-0.5">
        {/* Badges */}
        <div className="flex items-center gap-2">
          <div className="h-4 w-14 rounded-full cf-shimmer" />
          <div className="h-4 w-28 rounded-full cf-shimmer" />
        </div>
        {/* Title */}
        <div className="h-3.5 w-4/5 rounded cf-shimmer" />
        <div className="h-3.5 w-2/5 rounded cf-shimmer" />
      </div>
    </div>

    {/* Body lines */}
    <div className="mt-4 space-y-2">
      <div className="h-2.5 w-full rounded cf-shimmer" />
      <div className="h-2.5 w-11/12 rounded cf-shimmer" />
      <div className="h-2.5 w-3/5 rounded cf-shimmer" />
    </div>

    {/* Evidence block */}
    <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50/70 p-3 space-y-2">
      <div className="h-2 w-24 rounded cf-shimmer" />
      <div className="h-2.5 w-full rounded cf-shimmer" />
      <div className="h-2.5 w-4/6 rounded cf-shimmer" />
    </div>

    {/* Footer actions */}
    <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
      <div className="h-3 w-20 rounded cf-shimmer" />
      <div className="h-8 w-28 rounded-lg cf-shimmer" />
    </div>
  </div>
);

const InsightsLoader = ({ message, cards = 2, showHeader = true }) => {
  const [msgIndex, setMsgIndex] = React.useState(0);

  React.useEffect(() => {
    if (message) return undefined;
    const id = setInterval(
      () => setMsgIndex((i) => (i + 1) % ROTATING_MESSAGES.length),
      2200
    );
    return () => clearInterval(id);
  }, [message]);

  return (
    <div className="space-y-4" role="status" aria-live="polite">
      {/* ── Dotted orbit + status text ── */}
      {showHeader && (
      <div className="flex flex-col items-center justify-center pt-6 pb-2">
        <div className="relative w-16 h-16 flex items-center justify-center">
          {/* Outer dotted ring, slow spin */}
          <span className="absolute inset-0 rounded-full border-2 border-dotted border-cyan-400/70 cf-spin-slow" />
          {/* Inner dotted ring, reverse spin */}
          <span className="absolute inset-2 rounded-full border-2 border-dotted border-indigo-400/60 cf-spin-reverse" />
          {/* Pulsing core */}
          <span className="w-5 h-5 rounded-full bg-gradient-to-br from-cyan-500 to-indigo-600 shadow-lg shadow-cyan-500/30 cf-pulse-core" />
        </div>

        {/* Bouncing dots */}
        <div className="flex items-center gap-1.5 mt-4">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-2 h-2 rounded-full bg-gradient-to-br from-cyan-500 to-indigo-600 cf-dot-bounce"
              style={{ animationDelay: `${i * 160}ms` }}
            />
          ))}
        </div>

        <p className="mt-3 text-xs font-semibold text-slate-500 text-center px-6 min-h-[16px] transition-opacity duration-300">
          {message || ROTATING_MESSAGES[msgIndex]}
        </p>
        <p className="mt-1 text-[10px] text-slate-400">
          This usually takes a few seconds
        </p>
      </div>
      )}

      {/* ── Skeleton cards ── */}
      {Array.from({ length: cards }).map((_, i) => (
        <SkeletonCard key={i} delay={i * 120} />
      ))}
    </div>
  );
};

export default InsightsLoader;
